import {
    ClaimApi,
    CoreFetch,
    FacilityApi,
    ListProviderFacilitiesRequest,
    ListProviderProvidersRequest,
    ProviderApi,
    SupportCreateFacilityPayloadToJSON,
    SupportCreateProviderPayloadToJSON,
    SupportFacility,
    SupportFacilityFromJSON,
    SupportProvider,
    SupportProviderFromJSON,
    SupportTag,
    SupportTagFromJSON,
    SupportUpdateFacilityPayloadToJSON,
    SupportUpdateProviderPayloadToJSON,
    TagType,
} from '../../api';
import { rosterApiPage } from './roster.api-page';
import { rosterCollectClaims } from './roster.claims-mapping';
import type { RosterProviderAppClients, RosterProviderAppWriteClients } from './roster.provider-app-source';
import { rosterCall, rosterRawRequest } from './roster.raw-request';
import { rosterRoute } from './roster.support-clients';
import type { RosterApplyResult, RosterRecordRef } from './roster.types';

/**
 * The live provider-app lists: the generated client behind `CoreFetch`, so requests carry the
 * session and pass the demo-mode guard like every other call (NS-14).
 *
 * Kept apart from `roster.provider-app-source.ts` so that file — and its tests — never touch the
 * network layer.
 */
export function createRosterProviderAppClients(): RosterProviderAppClients {
    return {
        listProviders: (request: ListProviderProvidersRequest) =>
            rosterCall(async () => rosterApiPage(await CoreFetch<ProviderApi>(ProviderApi).listProviderProvidersRaw(request)), 'GET'),
        listFacilities: (request: ListProviderFacilitiesRequest) =>
            rosterCall(async () => rosterApiPage(await CoreFetch<FacilityApi>(FacilityApi).listProviderFacilitiesRaw(request)), 'GET'),
    };
}

/**
 * The provider app's writes, tags and claims, on `/provider/…` routes shaped like the support ones.
 *
 * Which of these exist (probed on sandbox 2026-09-29, see `docs/roster-backend-asks.md`):
 * - EXIST: GET `/provider/provider/{id}`, `/provider/facility/{id}`, and the provider claims list.
 * - MISSING: POST/PUT `/provider/provider` and `/provider/facility` ("Invalid HTTP method used for
 *   this path"), `…/{id}/disable` and `/provider/tag…` ("Invalid path"), and the proposed
 *   `…/{id}/claims/reapply`, which exists on neither API.
 *
 * They are called anyway (user decision, 2026-09-29): a missing route fails where it is used and
 * the error names it. The generated client has no methods for the missing ones, so they go through
 * `rosterRawRequest` — the generated runtime, unedited (NS-16). Payloads use the support shapes; the
 * provider app has none of its own for these routes yet.
 */
export function createRosterProviderAppWriteClients(): RosterProviderAppWriteClients {
    const providers = (): ProviderApi => CoreFetch<ProviderApi>(ProviderApi);
    const facilities = (): FacilityApi => CoreFetch<FacilityApi>(FacilityApi);

    return {
        getProvider: id => rosterCall(async () => (await providers().getProviderProvider({ id })) as SupportProvider, 'GET'),
        createProvider: async payload =>
            SupportProviderFromJSON(
                await rosterRawRequest('POST', '/provider/provider', { body: SupportCreateProviderPayloadToJSON(payload) }),
            ),
        updateProvider: async (id, payload) => {
            await rosterRawRequest('PUT', `/provider/provider/${encodeURIComponent(id)}`, {
                body: SupportUpdateProviderPayloadToJSON(payload),
            });
        },
        disableProvider: async id => {
            await rosterRawRequest('POST', `/provider/provider/${encodeURIComponent(id)}/disable`);
        },
        getFacility: id => rosterCall(async () => (await facilities().getProviderFacility({ id })) as SupportFacility, 'GET'),
        createFacility: async payload =>
            SupportFacilityFromJSON(
                await rosterRawRequest('POST', '/provider/facility', { body: SupportCreateFacilityPayloadToJSON(payload) }),
            ),
        updateFacility: async (id, payload) => {
            await rosterRawRequest('PUT', `/provider/facility/${encodeURIComponent(id)}`, {
                body: SupportUpdateFacilityPayloadToJSON(payload),
            });
        },
        disableFacility: async id => {
            await rosterRawRequest('POST', `/provider/facility/${encodeURIComponent(id)}/disable`);
        },
        findTags: async name => {
            const found = await rosterRawRequest('GET', '/provider/tag', {
                query: {
                    filterName: [`EQ:${name}`],
                    filterActive: 'EQ:1',
                    filterTagType: [`EQ:${TagType.USER}`],
                    limit: 100,
                },
            });
            return (Array.isArray(found) ? found : []).map(tag => SupportTagFromJSON(tag)) as SupportTag[];
        },
        createTag: async name =>
            SupportTagFromJSON(await rosterRawRequest('POST', '/provider/tag', { body: { name, type: TagType.USER } })),
        recordTag: async (tagId, target) => {
            await rosterRawRequest('POST', `/provider/tag/${encodeURIComponent(tagId)}/record`, { body: target });
        },
        listClaims: (ref: RosterRecordRef) =>
            rosterCollectClaims((offset, limit) =>
                rosterCall(
                    async () =>
                        rosterApiPage(
                            await CoreFetch<ClaimApi>(ClaimApi).listProviderClaimsRaw({
                                ...(ref.tab === 'providers'
                                    ? { filterProviderId: [`EQ:${ref.id}`] }
                                    : { filterFacilityId: [`EQ:${ref.id}`] }),
                                offset,
                                limit,
                            }),
                        ),
                    'GET',
                ),
            ),
        // Proposed route — exists on neither API (docs/roster-backend-asks.md).
        reapplyClaims: async (ref: RosterRecordRef, claimIds: string[], options: { retransmit: boolean }) =>
            (await rosterRawRequest('POST', `/provider/${rosterRoute(ref)}/${encodeURIComponent(ref.id)}/claims/reapply`, {
                body: { claimIds, retransmit: options.retransmit },
            })) as RosterApplyResult,
    };
}
